import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ChevronDown, ChevronRight, Folder, Link2, Lock, Pencil, Plus, Trash2, UserPlus, Users, X } from "lucide-react";

import {
  archiveProject,
  addProjectMember,
  createProjectLink,
  createFixedActivityRow,
  generateClientPortalLink,
  deleteProjectMember,
  deleteProjectLink,
  getProjectById,
  updateProjectFinancials,
  pauseProject,
  getProjectCommandCenter,
  getProjectLinks,
  getProjectMembers,
  getAuditLogs,
  resumeProject,
  getTaskUserSuggestions,
  renameProjectLinkFolder,
  updateProjectLink,
  updateProjectMember,
  updateProjectCommandActivity,
  updateProjectStatus,
  getFixedProjectMilestones,
  financeMarkFixedMilestoneDelivered,
  financeAddFixedMilestone,
} from "@/lib/api";
import AppShell from "@/components/AppShell";
import { ProjectLifecycleDialogs } from "@/components/project/ProjectLifecycleDialogs";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/sonner";

const getCurrentMonth = () => {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  return `${now.getFullYear()}-${month}`;
};

const monthOptions = () =>
  [0, 1, 2, 3, 4, 5].map((offset) => {
    const date = new Date();
    date.setMonth(date.getMonth() - offset);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    return {
      value,
      short: date.toLocaleDateString(undefined, { month: "short" }),
      long: date.toLocaleDateString(undefined, { month: "short", year: "numeric" }),
    };
  });

const healthVisual = (score) => {
  if (score >= 75) {
    return { label: "Healthy", tone: "#10B981", soft: "#ECFDF5" };
  }
  if (score >= 55) {
    return { label: "Watch", tone: "#F59E0B", soft: "#FFFBEB" };
  }
  return { label: "Critical", tone: "#EF4444", soft: "#FEF2F2" };
};

const detectLinkVisual = (link) => {
  const value = `${link?.url || ""} ${link?.title || ""} ${link?.link_type || ""}`.toLowerCase();
  if (value.includes("docs.google.com/spreadsheets") || /\.(xlsx|xls|csv)(\?|$)/.test(value)) {
    return { key: "sheets", label: "SHEET", bg: "#E8F5E9", color: "#34A853" };
  }
  if (value.includes("docs.google.com/document") || /\.(doc|docx|txt|rtf)(\?|$)/.test(value)) {
    return { key: "docs", label: "DOC", bg: "#E3F2FD", color: "#4285F4" };
  }
  if (value.includes("docs.google.com/presentation") || /\.(ppt|pptx|key)(\?|$)/.test(value)) {
    return { key: "slides", label: "SLIDES", bg: "#FFF8E1", color: "#F9AB00" };
  }
  if (value.includes("docs.google.com/forms")) {
    return { key: "forms", label: "FORM", bg: "#F3E8FF", color: "#A142F4" };
  }
  return { key: "pdf", label: "PDF", bg: "#FFEBEE", color: "#EA4335" };
};

const statusChip = (item, deadlineDate) => {
  if (item.completed) return <span className="chip chip-ok">Completed</span>;
  if (deadlineDate) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const deadline = new Date(deadlineDate);
    deadline.setHours(0, 0, 0, 0);
    if (!Number.isNaN(deadline.getTime()) && deadline < today) {
      return <span className="chip chip-crit">Overdue</span>;
    }
  }
  return <span className="chip chip-mute">Pending</span>;
};

const emptyActivity = {
  completed: false,
  date_completed: null,
  deadline_date: null,
  work_link: null,
  assigned_to_email: null,
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const ASSIGNEE_MENTION_REGEX = /^@[a-z0-9._-]{2,}$/i;

const toAssigneeHandle = (rawValue = "") => {
  const cleaned = String(rawValue || "").trim().toLowerCase();
  if (!cleaned) return "";
  if (cleaned === "@assign") return "@assign";
  if (ASSIGNEE_MENTION_REGEX.test(cleaned)) return cleaned;

  const source = cleaned.startsWith("@") ? cleaned.slice(1) : cleaned;
  const normalized = source.split("@")[0].replace(/[^a-z0-9._-]+/g, "_").replace(/^_+|_+$/g, "");
  if (!normalized) return "";
  const handle = `@${normalized}`;
  return ASSIGNEE_MENTION_REGEX.test(handle) ? handle : "";
};

const isAssigneeValueProvided = (value) => {
  const cleaned = String(value || "").trim();
  return ASSIGNEE_MENTION_REGEX.test(cleaned) && cleaned.toLowerCase() !== "@assign";
};

const TEAM_ROLE_OPTIONS = [
  { value: "account_manager", label: "Account Manager" },
  { value: "writer", label: "Writer" },
  { value: "designer", label: "Designer" },
  { value: "editor", label: "Editor" },
  { value: "project_admin", label: "Project Admin" },
];

const TEAM_ROLE_LABEL = {
  super_admin: "Super Admin",
  project_admin: "Project Admin",
  account_manager: "Account Manager",
  writer: "Writer",
  designer: "Designer",
  editor: "Editor",
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const toReadableName = (value = "") =>
  String(value || "")
    .replace(/[._-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");

const formatMemberName = (email = "") => {
  const local = String(email || "").split("@")[0];
  return toReadableName(local) || email;
};

const memberInitials = (email = "") => {
  const text = formatMemberName(email);
  const chunks = text.split(" ").filter(Boolean);
  if (!chunks.length) return "NA";
  if (chunks.length === 1) return chunks[0].slice(0, 2).toUpperCase();
  return `${chunks[0][0]}${chunks[chunks.length - 1][0]}`.toUpperCase();
};

const slugifyProjectName = (value = "") =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export default function ProjectPage({ user, onLogout }) {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [members, setMembers] = useState([]);
  const [commandCenter, setCommandCenter] = useState(null);
  const [links, setLinks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [month, setMonth] = useState(getCurrentMonth());
  const [isAddLinkOpen, setIsAddLinkOpen] = useState(false);
  const [isManageTeamOpen, setIsManageTeamOpen] = useState(false);
  const [isViewerTeamOpen, setIsViewerTeamOpen] = useState(false);
  const [memberSearchQuery, setMemberSearchQuery] = useState("");
  const [memberSuggestions, setMemberSuggestions] = useState([]);
  const [selectedMemberSuggestion, setSelectedMemberSuggestion] = useState(null);
  const [selectedMemberRole, setSelectedMemberRole] = useState("account_manager");
  const [memberRoleDraftMap, setMemberRoleDraftMap] = useState({});
  const [isLoadingMemberSuggestions, setIsLoadingMemberSuggestions] = useState(false);
  const [busyMemberActionId, setBusyMemberActionId] = useState("");
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [linkDraft, setLinkDraft] = useState({ title: "", url: "", link_type: "Important Links" });
  const [expandedFolders, setExpandedFolders] = useState({});
  const [expandedSections, setExpandedSections] = useState({
    links: true,
    cvt: true,
    activity: true,
    fixedMilestones: true,
    risks: false,
    recentActivity: false,
  });
  const [recentAuditRows, setRecentAuditRows] = useState([]);
  const [deadlineDraftMap, setDeadlineDraftMap] = useState({});
  const [assigneeDraftMap, setAssigneeDraftMap] = useState({});
  const [rowLinkModal, setRowLinkModal] = useState({
    open: false,
    checklistKey: "",
    label: "",
    work_link: "",
    date_completed: todayIso(),
  });

  const [completionModal, setCompletionModal] = useState({
    open: false,
    checklistKey: "",
    label: "",
    deadline_date: "",
    date_completed: "",
    work_link: "",
    assigned_to_email: "",
  });
  const [isPortalLinkLoading, setIsPortalLinkLoading] = useState(false);
  const [isLifecycleLoading, setIsLifecycleLoading] = useState(false);
  const [isProjectPauseModalOpen, setIsProjectPauseModalOpen] = useState(false);
  const [isProjectArchiveModalOpen, setIsProjectArchiveModalOpen] = useState(false);
  const [isEditingProjectValue, setIsEditingProjectValue] = useState(false);
  const [projectValueDraft, setProjectValueDraft] = useState("0");
  const [isSavingProjectValue, setIsSavingProjectValue] = useState(false);
  const [newActivityCtaLabel, setNewActivityCtaLabel] = useState("");
  const [isAddingActivityCta, setIsAddingActivityCta] = useState(false);
  const [projectStatusDraft, setProjectStatusDraft] = useState("active");
  const [fixedMilestoneSnapshot, setFixedMilestoneSnapshot] = useState(null);
  const [isAddFixedMilestoneModalOpen, setIsAddFixedMilestoneModalOpen] = useState(false);
  const [newFixedMilestoneDraft, setNewFixedMilestoneDraft] = useState({
    milestone_no: 1,
    name: "",
    trigger: "",
    percentage: "",
    value: "",
    expected_delivery_date: "",
  });

  const loadFixedMilestones = async () => {
    try {
      const snapshot = await getFixedProjectMilestones(projectId);
      setFixedMilestoneSnapshot(snapshot);
    } catch (_error) {
      setFixedMilestoneSnapshot(null);
    }
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [projectRes, commandRes, linkRes, memberRes] = await Promise.all([
        getProjectById(projectId),
        getProjectCommandCenter(projectId, month),
        getProjectLinks(projectId),
        getProjectMembers(projectId),
      ]);
      setProject(projectRes);
      setCommandCenter(commandRes);
      setLinks(linkRes);
      setMembers(memberRes);
      if (String(projectRes?.billing_type || projectRes?.project_type || "retainer").toLowerCase() === "fixed") {
        await loadFixedMilestones();
      } else {
        setFixedMilestoneSnapshot(null);
      }
      if (user.role === "super_admin") {
        try {
          const auditResponse = await getAuditLogs({
            entity_type: "project",
            entity_id: projectId,
            page: 1,
            limit: 10,
          });
          setRecentAuditRows(auditResponse?.rows || []);
        } catch (_error) {
          setRecentAuditRows([]);
        }
      } else {
        setRecentAuditRows([]);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, month]);

  const canManageTeam = useMemo(
    () => {
      if (user.role === "super_admin") return true;
      if (project?.current_user_role === "project_admin") return true;
      if (project?.current_user_role === "account_manager") return true;
      const isLevelTwo = Number(user.org_level || 0) === 2 || Boolean(user.is_pod_owner);
      const samePod = String(user.pod_name || "").trim().toLowerCase() === String(project?.pod_name || "").trim().toLowerCase();
      return Boolean(isLevelTwo && samePod);
    },
    [project?.current_user_role, project?.pod_name, user.is_pod_owner, user.org_level, user.pod_name, user.role],
  );

  const isViewerOnly = useMemo(
    () => Boolean(project?.viewer_only || (!canManageTeam && !project?.current_user_role)),
    [canManageTeam, project?.current_user_role, project?.viewer_only],
  );

  const projectAdminName = useMemo(() => {
    if (project?.project_admin?.name) return project.project_admin.name;
    if (project?.project_admin_name) return project.project_admin_name;
    return formatMemberName(project?.project_admin_email || "");
  }, [project?.project_admin?.name, project?.project_admin_email, project?.project_admin_name]);

  const projectAdminEmail = useMemo(
    () => project?.project_admin?.email || project?.project_admin_email || "",
    [project?.project_admin?.email, project?.project_admin_email],
  );

  const projectStatus = String(project?.status || "active").toLowerCase();
  const isFixedProject = String(project?.billing_type || project?.project_type || "retainer").toLowerCase() === "fixed";
  const canManageLifecycle = useMemo(() => {
    if (!project) return false;
    if (user.role === "super_admin") return true;
    const currentEmail = String(user.email || "").toLowerCase();
    const managerEmail = String(project.manager_email || "").toLowerCase();
    const adminEmail = String(project.project_admin_email || "").toLowerCase();
    if ([managerEmail, adminEmail].includes(currentEmail)) return true;

    const projectPodName = String(project.pod_name || "").trim().toLowerCase();
    const userPodName = String(user.pod_name || "").trim().toLowerCase();
    const isPodOwner = Boolean(user.is_pod_owner) || Number(user.org_level || 0) === 2;
    return Boolean(isPodOwner && projectPodName && userPodName && projectPodName === userPodName);
  }, [project, user.email, user.is_pod_owner, user.org_level, user.pod_name, user.role]);
  const canArchiveProject = canManageLifecycle;

  useEffect(() => {
    setProjectStatusDraft(projectStatus || "active");
  }, [projectStatus]);
  const projectCurrency = String(project?.currency || "INR").toUpperCase();
  const formattedProjectValue = useMemo(() => {
    const amount = Number(project?.project_value_monthly || 0);
    try {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: projectCurrency,
        maximumFractionDigits: 0,
      }).format(amount);
    } catch (_error) {
      return `₹${amount.toLocaleString("en-IN")}`;
    }
  }, [project?.project_value_monthly, projectCurrency]);

  const fixedMilestones = fixedMilestoneSnapshot?.milestones || [];
  const fixedContractValue = Number(fixedMilestoneSnapshot?.contracted || project?.project_value_monthly || 0);
  const fixedAllocatedPercent = Number((fixedMilestones || []).reduce((sum, item) => sum + Number(item.percentage || 0), 0).toFixed(2));

  const openAddFixedMilestoneModal = () => {
    const nextMilestoneNo = Math.max(0, ...fixedMilestones.map((item) => Number(item.milestone_no || 0))) + 1;
    setNewFixedMilestoneDraft({
      milestone_no: nextMilestoneNo,
      name: "",
      trigger: "",
      percentage: "",
      value: "",
      expected_delivery_date: "",
    });
    setIsAddFixedMilestoneModalOpen(true);
  };

  const saveNewFixedMilestone = async () => {
    try {
      await financeAddFixedMilestone(projectId, {
        milestone_no: Number(newFixedMilestoneDraft.milestone_no || 1),
        name: newFixedMilestoneDraft.name,
        trigger: newFixedMilestoneDraft.trigger,
        percentage: Number(newFixedMilestoneDraft.percentage || 0),
        value: Number(newFixedMilestoneDraft.value || 0),
        expected_delivery_date: newFixedMilestoneDraft.expected_delivery_date || null,
      });
      toast.success("Milestone added");
      setIsAddFixedMilestoneModalOpen(false);
      await loadFixedMilestones();
    } catch (error) {
      toast.error(error.message || "Unable to add milestone");
    }
  };

  const markMilestoneDelivered = async (milestoneId) => {
    try {
      await financeMarkFixedMilestoneDelivered(projectId, milestoneId, {
        delivered_at: new Date().toISOString().slice(0, 10),
      });
      toast.success("Milestone marked delivered");
      await loadFixedMilestones();
    } catch (error) {
      toast.error(error.message || "Unable to mark milestone delivered");
    }
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const viewerToastMessage = useMemo(
    () => `You have view-only access. Ask ${projectAdminName || "the project admin"} to add you as a member.`,
    [projectAdminName],
  );

  const requestAccessHref = useMemo(() => {
    if (!projectAdminEmail) return "";
    const subject = `Project access request — ${project?.name || project?.client_name || "Project"}`;
    return `mailto:${projectAdminEmail}?subject=${encodeURIComponent(subject)}`;
  }, [project?.client_name, project?.name, projectAdminEmail]);

  const guardViewerAction = () => {
    if (!isViewerOnly) return false;
    toast.error(viewerToastMessage);
    return true;
  };

  useEffect(() => {
    setMemberRoleDraftMap(
      members.reduce((acc, member) => {
        acc[member.id] = member.role;
        return acc;
      }, {}),
    );
  }, [members]);

  useEffect(() => {
    if (!canManageTeam && isManageTeamOpen) {
      setIsManageTeamOpen(false);
    }
  }, [canManageTeam, isManageTeamOpen]);

  useEffect(() => {
    if (!isViewerOnly) {
      setIsViewerTeamOpen(false);
    }
  }, [isViewerOnly]);

  useEffect(() => {
    const numericValue = Number(project?.project_value_monthly || 0);
    setProjectValueDraft(String(Number.isFinite(numericValue) ? numericValue : 0));
  }, [project?.project_value_monthly]);

  useEffect(() => {
    if (!isManageTeamOpen || !canManageTeam) {
      setIsLoadingMemberSuggestions(false);
      setMemberSuggestions([]);
      return undefined;
    }

    const query = memberSearchQuery.trim();
    if (query.length < 2) {
      setIsLoadingMemberSuggestions(false);
      setMemberSuggestions([]);
      return undefined;
    }

    let alive = true;
    const timerId = window.setTimeout(async () => {
      setIsLoadingMemberSuggestions(true);
      try {
        const suggestions = await getTaskUserSuggestions({ q: query, workspace_domain: "__all__" });
        if (!alive) return;
        const existingMemberEmails = new Set(members.map((member) => String(member.user_email || "").toLowerCase()));
        const filtered = suggestions.filter((item) => !existingMemberEmails.has(String(item.email || "").toLowerCase()));
        setMemberSuggestions(filtered);
      } catch (_error) {
        if (!alive) return;
        setMemberSuggestions([]);
      } finally {
        if (alive) setIsLoadingMemberSuggestions(false);
      }
    }, 220);

    return () => {
      alive = false;
      clearTimeout(timerId);
    };
  }, [canManageTeam, isManageTeamOpen, memberSearchQuery, members]);

  const activityRows = useMemo(() => {
    const checklist = commandCenter?.checklist || [];
    const activityStatus = commandCenter?.activity_status || {};
    return checklist.map((item) => ({
      ...item,
      tracker: item.tracker === "cvt" ? "cvt" : item.tracker === "activity" ? "activity" : "",
      activity: activityStatus[item.key] || emptyActivity,
    }));
  }, [commandCenter?.activity_status, commandCenter?.checklist]);

  const { cvtRows, actRows } = useMemo(() => {
    const hasTrackerFlags = activityRows.some((row) => row.tracker === "cvt" || row.tracker === "activity");
    const cvtBase = hasTrackerFlags
      ? activityRows.filter((row) => row.tracker === "cvt")
      : activityRows.slice(0, Math.ceil(activityRows.length / 2));
    const activityBase = hasTrackerFlags
      ? activityRows.filter((row) => row.tracker === "activity")
      : activityRows.slice(Math.ceil(activityRows.length / 2));

    return {
      cvtRows: cvtBase.map((row, index) => ({ ...row, sectionIndex: index + 1 })),
      actRows: activityBase.map((row, index) => ({ ...row, sectionIndex: index + 1 })),
    };
  }, [activityRows]);

  useEffect(() => {
    const deadlineMap = {};
    const assigneeMap = {};
    activityRows.forEach((row) => {
      deadlineMap[row.key] = row.activity.deadline_date || "";
      assigneeMap[row.key] = toAssigneeHandle(row.activity.assigned_to_email || "");
    });
    setDeadlineDraftMap(deadlineMap);
    setAssigneeDraftMap(assigneeMap);
  }, [activityRows]);

  const monthTabs = useMemo(() => monthOptions(), []);

  const linksByFolder = useMemo(() => {
    const map = new Map();
    links.forEach((link) => {
      const folder = link.folder_name || "Important Links";
      const existing = map.get(folder) || [];
      existing.push(link);
      map.set(folder, existing);
    });
    return [...map.entries()];
  }, [links]);

  useEffect(() => {
    setExpandedFolders((prev) => {
      const next = { ...prev };
      linksByFolder.forEach(([folder]) => {
        if (!(folder in next)) next[folder] = true;
      });
      return next;
    });
  }, [linksByFolder]);

  const healthTone = healthVisual(commandCenter?.health_score || 0);

  const completionPct = useMemo(() => {
    if (!activityRows.length) return 0;
    const done = activityRows.filter((row) => row.activity.completed).length;
    return Math.round((done / activityRows.length) * 100);
  }, [activityRows]);

  const cvtCompletionPct = useMemo(() => {
    if (!cvtRows.length) return 0;
    const done = cvtRows.filter((row) => row.activity.completed).length;
    return Math.round((done / cvtRows.length) * 100);
  }, [cvtRows]);

  const activityCompletionPct = useMemo(() => {
    if (!actRows.length) return 0;
    const done = actRows.filter((row) => row.activity.completed).length;
    return Math.round((done / actRows.length) * 100);
  }, [actRows]);

  const assigneeSuggestions = useMemo(() => {
    const options = new Set();
    members.forEach((member) => {
      if (member.user_email) {
        const emailHandle = toAssigneeHandle(member.user_email);
        if (emailHandle) options.add(emailHandle);
      }

      const nameHandle = toAssigneeHandle(`@${String(member.user_name || "").toLowerCase().replace(/[^a-z0-9]+/g, "_")}`);
      if (nameHandle) options.add(nameHandle);
    });
    return [...options];
  }, [members]);

  const computedRiskFlags = useMemo(() => {
    const existing = (commandCenter?.health_risks || []).map((risk) => ({ level: "critical", text: risk }));
    if (existing.length) return existing;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const overdueRows = activityRows.filter((row) => {
      const deadlineValue = row.activity.deadline_date || "";
      if (!deadlineValue || row.activity.completed) return false;
      const deadline = new Date(deadlineValue);
      deadline.setHours(0, 0, 0, 0);
      return !Number.isNaN(deadline.getTime()) && deadline < today;
    });

    const missingDeadlineCount = activityRows.filter((row) => !row.activity.deadline_date).length;
    const missingAssigneeCount = activityRows.filter((row) => !isAssigneeValueProvided(row.activity.assigned_to_email)).length;

    const generated = [];
    if ((commandCenter?.health_score || 0) < 55) {
      generated.push({ level: "critical", text: "Health score is below target. Review overdue and pending items." });
    }
    if (overdueRows.length > 0) {
      generated.push({ level: "critical", text: `${overdueRows.length} tracker item(s) are overdue${isFixedProject ? "." : " this month."}` });
    }
    if (missingDeadlineCount > 0) {
      generated.push({ level: "warn", text: `${missingDeadlineCount} item(s) do not have deadlines yet.` });
    }
    if (missingAssigneeCount > 0) {
      generated.push({ level: "warn", text: `${missingAssigneeCount} item(s) are missing assignee names.` });
    }
    if (completionPct < 40) {
      generated.push({ level: "warn", text: `Tracker completion is ${completionPct}. Keep completion above 60.` });
    }

    return generated;
  }, [activityRows, commandCenter?.health_risks, commandCenter?.health_score, completionPct, isFixedProject]);

  const folderOptions = useMemo(() => {
    const options = ["Important Links", ...linksByFolder.map(([folder]) => folder)];
    return [...new Set(options)];
  }, [linksByFolder]);

  const teamSummaryText = useMemo(() => {
    if (!members.length) return "No members assigned";
    return members.map((member) => formatMemberName(member.user_email)).join(" · ");
  }, [members]);

  const displayedTeamAvatars = useMemo(() => members.slice(0, 5), [members]);

  const roleOptionsForCurrentUser = useMemo(
    () => (user.role === "super_admin" ? TEAM_ROLE_OPTIONS : TEAM_ROLE_OPTIONS.filter((option) => option.value !== "project_admin")),
    [user.role],
  );

  const manualEmailCandidate = useMemo(() => {
    const value = memberSearchQuery.trim().toLowerCase();
    if (!EMAIL_REGEX.test(value)) return "";
    return value;
  }, [memberSearchQuery]);

  const canSubmitMemberInvite = useMemo(() => {
    if (selectedMemberSuggestion?.email) return true;
    if (!manualEmailCandidate) return false;
    const existingEmails = new Set(members.map((member) => String(member.user_email || "").toLowerCase()));
    return !existingEmails.has(manualEmailCandidate);
  }, [manualEmailCandidate, members, selectedMemberSuggestion?.email]);

  const openManageTeamDrawer = () => {
    if (canManageTeam) {
      setIsManageTeamOpen(true);
      return;
    }
    setIsViewerTeamOpen(true);
  };

  const closeManageTeamDrawer = () => {
    setIsManageTeamOpen(false);
    setMemberSearchQuery("");
    setSelectedMemberSuggestion(null);
    setMemberSuggestions([]);
    setSelectedMemberRole("account_manager");
    setIsLoadingMemberSuggestions(false);
  };

  const closeViewerTeamDrawer = () => {
    setIsViewerTeamOpen(false);
  };

  const handleMemberSearchChange = (value) => {
    setMemberSearchQuery(value);
    setSelectedMemberSuggestion(null);
  };

  const selectMemberSuggestion = (suggestion) => {
    setSelectedMemberSuggestion(suggestion);
    setMemberSearchQuery(suggestion.email || "");
    setMemberSuggestions([]);
  };

  const handleAddMemberToProject = async () => {
    if (!canManageTeam) return;

    const selectedEmail = selectedMemberSuggestion?.email ? String(selectedMemberSuggestion.email).toLowerCase() : "";
    const emailToAdd = selectedEmail || manualEmailCandidate;
    if (!emailToAdd) {
      toast.error("Search and select a member email first.");
      return;
    }

    const existingEmails = new Set(members.map((member) => String(member.user_email || "").toLowerCase()));
    if (existingEmails.has(emailToAdd)) {
      toast.error("This user is already part of the project.");
      return;
    }

    const fallbackName = formatMemberName(emailToAdd);
    const payload = {
      name: selectedMemberSuggestion?.name || fallbackName || emailToAdd,
      email: emailToAdd,
      role: selectedMemberRole,
    };

    try {
      setIsAddingMember(true);
      await addProjectMember(projectId, payload);
      toast.success("Project member added");
      setMemberSearchQuery("");
      setSelectedMemberSuggestion(null);
      setMemberSuggestions([]);
      setSelectedMemberRole("account_manager");
      await loadData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsAddingMember(false);
    }
  };

  const saveMemberRole = async (member) => {
    if (!canManageTeam) return;
    const nextRole = memberRoleDraftMap[member.id] || member.role;
    if (nextRole === member.role) return;

    try {
      setBusyMemberActionId(`role-${member.id}`);
      await updateProjectMember(projectId, member.id, { role: nextRole });
      toast.success("Team member role updated");
      await loadData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusyMemberActionId("");
    }
  };

  const removeMemberFromProject = async (member) => {
    if (!canManageTeam) return;
    const confirmed = window.confirm(`Remove ${member.user_email} from this project?`);
    if (!confirmed) return;

    try {
      setBusyMemberActionId(`remove-${member.id}`);
      await deleteProjectMember(projectId, member.id);
      toast.success("Member removed from project");
      await loadData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusyMemberActionId("");
    }
  };

  const updateRowActivity = async (checklistKey, payload, successMessage = "Updated") => {
    if (guardViewerAction()) return;
    await updateProjectCommandActivity(projectId, {
      month,
      checklist_key: checklistKey,
      ...payload,
    });
    if (successMessage) toast.success(successMessage);
    await loadData();
  };

  const saveDeadline = async (row) => {
    const nextDeadline = (deadlineDraftMap[row.key] || "").trim();
    const previousDeadline = row.activity.deadline_date || "";
    if (nextDeadline === previousDeadline) return;
    try {
      await updateRowActivity(row.key, { deadline_date: nextDeadline || null }, "Deadline updated");
    } catch (error) {
      toast.error(error.message);
    }
  };

  const saveAssignee = async (row) => {
    const draftValue = (assigneeDraftMap[row.key] || "").trim();
    const nextAssignee = draftValue ? toAssigneeHandle(draftValue) : "@assign";
    if (draftValue && !nextAssignee) {
      toast.error("Please enter a valid assignee name or email");
      return;
    }

    const previousAssignee = row.activity.assigned_to_email || "";
    if (nextAssignee === previousAssignee) return;
    try {
      await updateRowActivity(row.key, { assigned_to_email: nextAssignee || null }, "Assignee updated");
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDoneToggle = async (row, checked) => {
    if (isViewerOnly) return;
    if (checked) {
      if (row.activity.completed) return;
      openCompletionModal(row);
      return;
    }
    if (!row.activity.completed) return;
    try {
      await updateRowActivity(
        row.key,
        {
          completed: false,
          date_completed: null,
        },
        "Marked as not done",
      );
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleAddActivityCta = async () => {
    if (!isFixedProject || !canManageLifecycle) return;
    const label = String(newActivityCtaLabel || "").trim();
    if (!label) {
      toast.error("Activity row title is required");
      return;
    }
    try {
      setIsAddingActivityCta(true);
      await createFixedActivityRow(projectId, { label });
      toast.success("Activity row added");
      setNewActivityCtaLabel("");
      await loadData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsAddingActivityCta(false);
    }
  };

  const handleProjectStatusSelectChange = async (nextValue) => {
    if (!isFixedProject || !canManageLifecycle) return;
    const nextStatus = String(nextValue || "active").toLowerCase();
    const currentStatus = projectStatus;
    setProjectStatusDraft(nextStatus);
    if (nextStatus === currentStatus) return;

    const effectiveMonth = window.prompt("Apply status impact from month (YYYY-MM)", getCurrentMonth());
    if (effectiveMonth === null) {
      setProjectStatusDraft(currentStatus);
      return;
    }

    try {
      setIsLifecycleLoading(true);
      await updateProjectStatus(projectId, {
        status: nextStatus,
        reason: `Updated from project page status dropdown (${currentStatus} → ${nextStatus})`,
        effective_month: effectiveMonth,
      });
      toast.success("Project status updated");
      await loadData();
    } catch (error) {
      toast.error(error.message);
      setProjectStatusDraft(currentStatus);
    } finally {
      setIsLifecycleLoading(false);
    }
  };

  const openRowLinkModal = (row) => {
    if (guardViewerAction()) return;
    setRowLinkModal({
      open: true,
      checklistKey: row.key,
      label: row.label,
      work_link: row.activity.work_link || "",
      date_completed: row.activity.date_completed || todayIso(),
    });
  };

  const submitRowLink = async () => {
    if (guardViewerAction()) return;
    const workLink = rowLinkModal.work_link.trim();
    if (!workLink) {
      toast.error("URL is required");
      return;
    }
    try {
      await updateRowActivity(
        rowLinkModal.checklistKey,
        {
          work_link: workLink,
          date_completed: rowLinkModal.date_completed || todayIso(),
        },
        "Work link added",
      );
      setRowLinkModal((prev) => ({ ...prev, open: false }));
    } catch (error) {
      toast.error(error.message);
    }
  };

  const openCompletionModal = (row) => {
    if (guardViewerAction()) return;
    setCompletionModal({
      open: true,
      checklistKey: row.key,
      label: row.label,
      deadline_date: deadlineDraftMap[row.key] || row.activity.deadline_date || "",
      date_completed: row.activity.date_completed || todayIso(),
      work_link: row.activity.work_link || "",
      assigned_to_email: toAssigneeHandle(assigneeDraftMap[row.key] || row.activity.assigned_to_email || ""),
    });
  };

  const submitCompletion = async () => {
    if (guardViewerAction()) return;
    if (!completionModal.deadline_date) {
      toast.error("Deadline is mandatory before marking done.");
      return;
    }
    if (!isAssigneeValueProvided(completionModal.assigned_to_email)) {
      toast.error("Assigned to is mandatory before marking done.");
      return;
    }
    if (!completionModal.date_completed || !completionModal.work_link) {
      toast.error("Date and work link are mandatory.");
      return;
    }
    try {
      await updateProjectCommandActivity(projectId, {
        month,
        checklist_key: completionModal.checklistKey,
        is_done: true,
        deadline_date: completionModal.deadline_date,
        completed_date: completionModal.date_completed,
        work_link: completionModal.work_link,
        assigned_to_email: completionModal.assigned_to_email,
        notes: "Marked complete from tracker",
      });
      toast.success("Tracker item marked done");
      setCompletionModal((prev) => ({ ...prev, open: false }));
      loadData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCreateLink = async (event) => {
    event.preventDefault();
    if (guardViewerAction()) return;
    try {
      const created = await createProjectLink(projectId, linkDraft);
      if (created?.imported_count && created.imported_count > 1) {
        toast.success(`${created.imported_count} files imported from folder`);
      } else {
        toast.success("Link added");
      }
      setLinkDraft({ title: "", url: "", link_type: "Important Links" });
      setIsAddLinkOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteLink = async (linkId) => {
    if (guardViewerAction()) return;
    try {
      await deleteProjectLink(projectId, linkId);
      toast.success("Link removed");
      loadData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleRenameLink = async (link) => {
    if (guardViewerAction()) return;
    const nextTitle = window.prompt("Rename link", link.title || "");
    if (nextTitle == null) return;
    const trimmed = nextTitle.trim();
    if (!trimmed || trimmed === link.title) return;
    try {
      await updateProjectLink(projectId, link.id, { title: trimmed });
      toast.success("Link renamed");
      loadData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleRenameFolder = async (folderName) => {
    if (guardViewerAction()) return;
    const nextFolder = window.prompt("Rename folder", folderName || "");
    if (nextFolder == null) return;
    const trimmed = nextFolder.trim();
    if (!trimmed || trimmed === folderName) return;
    try {
      await renameProjectLinkFolder(projectId, folderName, { new_folder_name: trimmed });
      toast.success("Folder renamed");
      loadData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCopyClientPortalLink = async () => {
    if (!canManageLifecycle) {
      toast.error("Only project managers can generate client portal links");
      return;
    }
    setIsPortalLinkLoading(true);
    try {
      const response = await generateClientPortalLink(projectId, { expires_in_days: 30 });
      const linkValue = response?.portal_link || "";
      if (!linkValue) throw new Error("Unable to generate portal link");
      await navigator.clipboard.writeText(linkValue);
      toast.success("Client portal link copied");
    } catch (error) {
      toast.error(error.message || "Failed to copy client link");
    } finally {
      setIsPortalLinkLoading(false);
    }
  };

  const handlePauseProject = async () => {
    if (!canManageLifecycle) {
      toast.error("You do not have access to pause this project");
      return;
    }
    setIsProjectPauseModalOpen(true);
  };

  const confirmPauseProject = async (form) => {
    const reasonLabel = String(form?.reason || "").trim();
    if (!reasonLabel) {
      toast.error("Please select a pause reason");
      return;
    }
    const notes = String(form?.notes || "").trim();
    const reason = notes ? `${reasonLabel} — ${notes}` : reasonLabel;
    const resume_expected = String(form?.resume_expected || "").trim() || null;
    const effective_month = String(form?.effective_month || "").trim() || getCurrentMonth();
    setIsLifecycleLoading(true);
    try {
      await pauseProject(projectId, { reason, resume_expected, effective_month });
      toast.success("Project paused");
      setIsProjectPauseModalOpen(false);
      await loadData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLifecycleLoading(false);
    }
  };

  const handleResumeProject = async () => {
    if (!canManageLifecycle) {
      toast.error("You do not have access to resume this project");
      return;
    }
    const effectiveMonth = window.prompt("Apply resume impact from month (YYYY-MM)", getCurrentMonth());
    if (effectiveMonth === null) return;
    setIsLifecycleLoading(true);
    try {
      await resumeProject(projectId, { reason: "Resumed from project header", effective_month: effectiveMonth });
      toast.success("Project resumed");
      await loadData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLifecycleLoading(false);
    }
  };

  const handleArchiveProject = async () => {
    if (!canArchiveProject) {
      toast.error("You do not have access to archive this project");
      return;
    }
    setIsProjectArchiveModalOpen(true);
  };

  const confirmArchiveProject = async (form) => {
    const reason = String(form?.reason || "").trim();
    const confirmName = String(form?.confirm_name || "").trim();
    const effective_month = String(form?.effective_month || "").trim() || getCurrentMonth();
    if (!reason || !confirmName) {
      toast.error("Reason and confirmation name are required");
      return;
    }
    setIsLifecycleLoading(true);
    try {
      await archiveProject(projectId, { confirm_name: confirmName, reason, effective_month });
      toast.success("Project archived");
      setIsProjectArchiveModalOpen(false);
      await loadData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLifecycleLoading(false);
    }
  };

  const openUpdateClientDashboard = () => {
    if (!project?.id) return;
    const slug = slugifyProjectName(project?.client_name || project?.name || project?.id);
    navigate(`/project/${slug}/update-client-view`, {
      state: {
        projectId: project.id,
      },
    });
  };

  const handleSaveProjectValue = async () => {
    if (!canManageLifecycle) {
      toast.error("Only project managers can update project value");
      return;
    }
    const numericValue = Number(projectValueDraft);
    if (!Number.isFinite(numericValue) || numericValue < 0) {
      toast.error("Enter a valid monthly project value");
      return;
    }
    const effectiveMonth = window.prompt("Apply project value change from month (YYYY-MM)", getCurrentMonth());
    if (effectiveMonth === null) return;

    setIsSavingProjectValue(true);
    try {
      const updated = await updateProjectFinancials(projectId, { project_value_monthly: numericValue, effective_month: effectiveMonth });
      setProject(updated);
      setProjectValueDraft(String(Number(updated?.project_value_monthly || numericValue)));
      setIsEditingProjectValue(false);
      toast.success("Project value updated and synced");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSavingProjectValue(false);
    }
  };

  if (isLoading) {
    return (
      <AppShell user={user} onLogout={onLogout}>
        <div className="jump-card p-6 text-sm text-slate-500" data-testid="project-page-loading-state">Loading project details...</div>
      </AppShell>
    );
  }

  if (!project || !commandCenter) {
    return (
      <AppShell user={user} onLogout={onLogout}>
        <div className="jump-card p-6 text-sm text-slate-500" data-testid="project-page-empty-state">Project not found.</div>
      </AppShell>
    );
  }

  return (
    <AppShell user={user} onLogout={onLogout}>
      {isViewerOnly ? (
        <div
          className="sticky z-20 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
          style={{ top: 8, marginBottom: 12 }}
          data-testid="project-detail-viewer-banner"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-white px-2 py-0.5 text-xs font-semibold text-amber-800" data-testid="project-detail-viewer-banner-badge">
              <Lock className="h-3 w-3" /> Viewer only
            </span>
            <p className="text-sm text-amber-800" data-testid="project-detail-viewer-banner-text">
              You can see this project but cannot make changes. Contact {projectAdminName || "the project admin"} to be added as a member.
            </p>
            <button
              type="button"
              className="ml-auto text-xs font-semibold text-amber-800 underline"
              onClick={() => navigate("/clients")}
              data-testid="project-detail-viewer-banner-back-to-clients"
            >
              ← Back to clients
            </button>
          </div>
        </div>
      ) : null}

      <section className="space-y-4" data-testid="project-detail-page-root">
        <div className="jump-card flex flex-wrap items-center gap-3 p-4" data-testid="project-detail-header-card">
          <Button variant="outline" size="sm" onClick={() => navigate("/projects")} data-testid="project-detail-back-button">
            ← Projects
          </Button>
          <div className="flex-1 min-w-[220px]" data-testid="project-detail-client-wrap">
            <p className="text-xs text-slate-500">{project.client_name} · {project.service_type}</p>
            <h2 className="text-2xl font-semibold text-slate-900" data-testid="project-detail-client-title">
              Client: <span className="text-indigo-600">{project.client_name}</span>
              <span
                className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${projectStatus === "active" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : projectStatus === "paused" ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-rose-50 text-rose-700 border border-rose-200"}`}
                data-testid="project-detail-status-chip"
              >
                {projectStatus}
              </span>
              {isViewerOnly ? (
                <span className="ml-2 inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800" data-testid="project-detail-view-only-tag">
                  View only
                </span>
              ) : null}
            </h2>
            <div className="mt-1 flex flex-wrap items-center gap-3" data-testid="project-detail-team-meta-row">
              <button
                type="button"
                className="flex items-center -space-x-2"
                style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer" }}
                onClick={openManageTeamDrawer}
                data-testid="project-detail-team-avatars-button"
              >
                {displayedTeamAvatars.length ? (
                  displayedTeamAvatars.map((member, index) => (
                    <div
                      key={member.id}
                      className="avatar avatar-sm avatar-d border-2 border-white"
                      style={{ boxShadow: "var(--shadow-soft)", zIndex: displayedTeamAvatars.length - index }}
                      title={`${formatMemberName(member.user_email)} · ${TEAM_ROLE_LABEL[member.role] || member.role}`}
                      data-testid={`project-detail-team-avatar-${member.id}`}
                    >
                      {memberInitials(member.user_email)}
                    </div>
                  ))
                ) : (
                  <div className="chip chip-mute" data-testid="project-detail-team-avatar-empty">No members</div>
                )}
                {members.length > displayedTeamAvatars.length ? (
                  <div className="chip chip-mute" style={{ marginLeft: 8 }} data-testid="project-detail-team-avatars-more-count">
                    +{members.length - displayedTeamAvatars.length}
                  </div>
                ) : null}
              </button>
              <p className="text-xs text-slate-500" data-testid="project-detail-team-meta">
                Team: {teamSummaryText}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2" data-testid="project-detail-header-actions">
            {canManageTeam ? (
              <Button onClick={openManageTeamDrawer} data-testid="project-detail-manage-team-button">
                + Manage Team
              </Button>
            ) : isViewerOnly ? (
              <span className="chip chip-warn" data-testid="project-detail-viewing-only-badge">Viewing only</span>
            ) : null}

            {!isViewerOnly ? (
              <Button
                onClick={openUpdateClientDashboard}
                style={{ background: "#0EA5E9", borderColor: "#0EA5E9", color: "#fff" }}
                data-testid="project-detail-update-client-dashboard-button"
              >
                Update Client Dashboard
              </Button>
            ) : null}

            {canManageLifecycle ? (
              <Button
                variant="outline"
                onClick={handleCopyClientPortalLink}
                disabled={isPortalLinkLoading}
                data-testid="project-detail-copy-client-link-button"
              >
                {isPortalLinkLoading ? "Copying..." : "Copy client link"}
              </Button>
            ) : null}

            {canManageLifecycle && projectStatus !== "archived" ? (
              <Button
                variant="outline"
                onClick={projectStatus === "paused" ? handleResumeProject : handlePauseProject}
                disabled={isLifecycleLoading}
                data-testid="project-detail-pause-resume-button"
              >
                {projectStatus === "paused" ? "Resume" : "Pause"}
              </Button>
            ) : null}

            {canArchiveProject && projectStatus !== "archived" ? (
              <Button
                variant="outline"
                onClick={handleArchiveProject}
                disabled={isLifecycleLoading}
                data-testid="project-detail-archive-button"
              >
                Archive
              </Button>
            ) : null}
          </div>
        </div>

        <div className="jump-card flex flex-wrap items-center justify-between gap-3 p-4" data-testid="project-detail-header-metrics-card">
          {!isFixedProject ? (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 4, border: "1px solid var(--paper-200)", borderRadius: 999, padding: 3 }} data-testid="project-detail-month-tabs">
              {monthTabs.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  className={`btn btn-sm ${month === tab.value ? "btn-primary" : ""}`}
                  onClick={() => setMonth(tab.value)}
                  data-testid={`project-detail-month-tab-${tab.value}`}
                >
                  {tab.short}
                </button>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-2" data-testid="project-detail-fixed-project-status-wrap">
              <label htmlFor="project-detail-fixed-project-status-select" className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                Project status
              </label>
              {canManageLifecycle ? (
                <select
                  id="project-detail-fixed-project-status-select"
                  className="field-select h-10 min-w-[180px]"
                  value={projectStatusDraft}
                  onChange={(event) => handleProjectStatusSelectChange(event.target.value)}
                  disabled={isLifecycleLoading}
                  data-testid="project-detail-fixed-project-status-select"
                >
                  <option value="active">active</option>
                  <option value="paused">paused</option>
                  <option value="completed">completed</option>
                  <option value="archived">archived</option>
                </select>
              ) : (
                <span className="chip chip-mute" data-testid="project-detail-fixed-project-status-readonly">
                  {projectStatus}
                </span>
              )}
            </div>
          )}

          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              border: "1px solid var(--paper-200)",
              borderRadius: 14,
              padding: "8px 10px",
              background: "#fff",
              minWidth: 170,
            }}
            data-testid="project-detail-health-badge"
          >
            <svg width="52" height="52" viewBox="0 0 52 52" data-testid="project-detail-health-gauge">
              <circle cx="26" cy="26" r="20" fill="none" stroke="#E5E7EB" strokeWidth="6" />
              <circle
                cx="26"
                cy="26"
                r="20"
                fill="none"
                stroke={healthTone.tone}
                strokeWidth="6"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 20}`}
                strokeDashoffset={`${2 * Math.PI * 20 * (1 - Math.min(100, commandCenter.health_score || 0) / 100)}`}
                transform="rotate(-90 26 26)"
              />
              <text x="26" y="31" textAnchor="middle" fontSize="13" fontWeight="700" fill={healthTone.tone}>
                {commandCenter.health_score}
              </text>
            </svg>
            <div>
              <div style={{ fontSize: 12, color: "var(--mute-500)", textTransform: "uppercase", letterSpacing: ".06em" }}>Health</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: healthTone.tone }}>{healthTone.label}</div>
            </div>
          </div>
        </div>

        <div className="jump-card border border-sky-200 bg-sky-50 p-4" data-testid="project-detail-value-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500" data-testid="project-detail-value-label">{isFixedProject ? "Project Value" : "Project Value / Month"}</p>
              {!isEditingProjectValue ? (
                <p className="text-3xl font-semibold text-slate-900" data-testid="project-detail-value-display">{formattedProjectValue}</p>
              ) : (
                <div className="flex items-center gap-2" data-testid="project-detail-value-editor-wrap">
                  <Input
                    type="number"
                    min="0"
                    step="100"
                    value={projectValueDraft}
                    onChange={(event) => setProjectValueDraft(event.target.value)}
                    className="w-44"
                    data-testid="project-detail-value-input"
                  />
                  <Button
                    onClick={handleSaveProjectValue}
                    disabled={isSavingProjectValue}
                    data-testid="project-detail-value-save-button"
                  >
                    {isSavingProjectValue ? "Saving..." : "Save"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsEditingProjectValue(false);
                      setProjectValueDraft(String(Number(project?.project_value_monthly || 0)));
                    }}
                    data-testid="project-detail-value-cancel-button"
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
            {canManageLifecycle && !isEditingProjectValue ? (
              <Button
                variant="outline"
                onClick={() => setIsEditingProjectValue(true)}
                data-testid="project-detail-value-edit-button"
              >
                <Pencil className="mr-1 h-4 w-4" /> Edit value
              </Button>
            ) : null}
          </div>
          <p className="mt-2 text-xs text-slate-500" data-testid="project-detail-value-helper">This value syncs to client dashboard and billing calculations automatically.</p>
        </div>

        <Card className="jump-card border border-rose-200 bg-rose-50" data-testid="project-detail-risk-card">
          <CardHeader style={{ paddingBottom: 12 }}>
            <button
              type="button"
              onClick={() => toggleSection("risks")}
              className="flex w-full items-center justify-between gap-2 text-left"
              style={{ border: "none", background: "transparent", padding: 0 }}
              data-testid="project-detail-risk-accordion-toggle"
            >
              <div className="flex items-center gap-2 text-rose-700">
                <AlertTriangle className="h-4 w-4" />
                <p className="font-semibold">Risks &amp; flags</p>
                <Badge className="bg-rose-100 text-rose-700" data-testid="project-detail-risk-count-badge">
                  {computedRiskFlags.length}
                </Badge>
              </div>
              {expandedSections.risks ? <ChevronDown className="h-4 w-4 text-rose-700" /> : <ChevronRight className="h-4 w-4 text-rose-700" />}
            </button>
          </CardHeader>
          {expandedSections.risks ? (
            <CardContent>
              <div className="space-y-1 text-sm">
                {computedRiskFlags.length === 0 ? (
                  <p className="text-slate-500" data-testid="project-detail-risk-empty">No current risks.</p>
                ) : (
                  computedRiskFlags.map((risk, index) => (
                    <p key={`${risk.text}-${index}`} className={risk.level === "critical" ? "text-rose-700" : "text-amber-700"} data-testid={`project-detail-risk-item-${index}`}>
                      • {risk.text}
                    </p>
                  ))
                )}
                <p className="pt-1 text-xs text-slate-500" data-testid="project-detail-risk-helper">
                  Auto-filled from health score trends, overdue tracker rows, missing deadlines/assignees, and command-center risk rules.
                </p>
              </div>
            </CardContent>
          ) : null}
        </Card>

        <div className="jump-card flex items-center gap-3 p-4" style={{ borderColor: healthTone.tone, background: healthTone.soft }} data-testid="project-detail-health-progress-card">
          <div>
            <p className="text-sm font-semibold" style={{ color: healthTone.tone }} data-testid="project-detail-health-progress-title">{isFixedProject ? "Project Health Score" : "Monthly Health Score"}</p>
            <p className="text-xs" style={{ color: healthTone.tone }} data-testid="project-detail-health-progress-subtitle">Operational 40 · Client Value 60</p>
          </div>
          <div className="jump-progress-track flex-1" data-testid="project-detail-health-track">
            <div className="jump-progress-fill" style={{ width: `${Math.min(100, commandCenter.health_score || 0)}%`, background: healthTone.tone }} data-testid="project-detail-health-fill" />
          </div>
          <p className="text-2xl font-bold" style={{ color: healthTone.tone }} data-testid="project-detail-health-value">{commandCenter.health_score}</p>
        </div>

        <Card className="jump-card border-0" data-testid="project-detail-links-card">
          <CardHeader style={{ borderBottom: "1px solid var(--paper-200)", paddingBottom: 14 }}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => toggleSection("links")}
                className="flex items-center gap-2 text-left"
                style={{ border: "none", background: "transparent", padding: 0 }}
                data-testid="project-detail-links-accordion-toggle"
              >
                {expandedSections.links ? <ChevronDown className="h-4 w-4 text-slate-600" /> : <ChevronRight className="h-4 w-4 text-slate-600" />}
                <CardTitle className="flex items-center gap-2 text-lg" data-testid="project-detail-links-title">
                  <Link2 className="h-4 w-4" />
                  Important Links
                </CardTitle>
              </button>
              <div className="flex items-center gap-2">
                <span className="chip chip-mute" data-testid="project-detail-links-total-chip">{links.length} files</span>
                {!isFixedProject ? (
                  <span className="chip chip-brand" data-testid="project-detail-month-badge">{monthTabs.find((tab) => tab.value === month)?.long || month}</span>
                ) : null}
                {isViewerOnly ? (
                  <span className="chip chip-warn" data-testid="project-detail-links-read-only-chip">Read only</span>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setLinkDraft((prev) => ({ ...prev, title: "", url: "", link_type: "" }));
                        setIsAddLinkOpen(true);
                      }}
                      data-testid="project-detail-open-new-folder-button"
                    >
                      <Plus className="mr-1 h-4 w-4" /> New Folder
                    </Button>
                    <Button variant="outline" onClick={() => setIsAddLinkOpen(true)} data-testid="project-detail-open-add-link-button">
                      <Plus className="mr-1 h-4 w-4" /> Add Link
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardHeader>
          {expandedSections.links ? (
          <CardContent className="space-y-3" style={{ paddingTop: 14 }} data-testid="project-detail-links-content">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }} data-testid="project-detail-link-type-legend">
              <span className="chip" style={{ background: "#E8F5E9", color: "#34A853" }} data-testid="project-detail-link-legend-sheets">🟢 Google Sheets</span>
              <span className="chip" style={{ background: "#E3F2FD", color: "#4285F4" }} data-testid="project-detail-link-legend-docs">🔵 Google Docs</span>
              <span className="chip" style={{ background: "#FFF8E1", color: "#F9AB00" }} data-testid="project-detail-link-legend-slides">🟡 Google Slides</span>
              <span className="chip" style={{ background: "#FFEBEE", color: "#EA4335" }} data-testid="project-detail-link-legend-pdf">🔴 PDF / Drive File</span>
              <span className="chip" style={{ background: "#F3E8FF", color: "#A142F4" }} data-testid="project-detail-link-legend-forms">🟣 Google Forms</span>
            </div>
            {linksByFolder.length === 0 ? (
              <p className="text-sm text-slate-500" data-testid="project-detail-links-empty">No links added yet.</p>
            ) : (
              linksByFolder.map(([folder, rows]) => (
                <div key={folder} className="rounded-xl border border-slate-200" data-testid={`project-detail-link-folder-${folder.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}>
                  <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2" data-testid={`project-detail-link-folder-header-${folder.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}>
                    <button
                      type="button"
                      onClick={() => setExpandedFolders((prev) => ({ ...prev, [folder]: !prev[folder] }))}
                      style={{ border: "none", background: "transparent", padding: 0, display: "inline-flex", cursor: "pointer" }}
                      data-testid={`project-detail-link-folder-toggle-${folder.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
                    >
                      {expandedFolders[folder] ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                    </button>
                    <Folder className="h-4 w-4 text-slate-500" />
                    <p className="text-sm font-medium text-slate-900">{folder}</p>
                    <span className="ml-auto text-xs text-slate-500" data-testid={`project-detail-link-folder-count-${folder.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}>
                      {rows.length} files
                    </span>
                    {!isViewerOnly ? (
                      <div className="ml-auto flex items-center gap-1" data-testid={`project-detail-link-folder-actions-${folder.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}>
                        <button
                          type="button"
                          className="btn btn-sm"
                          style={{ height: 26, paddingInline: 8 }}
                          onClick={() => handleRenameFolder(folder)}
                          data-testid={`project-detail-link-folder-rename-${folder.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm"
                          style={{ height: 26, paddingInline: 8 }}
                          onClick={() => {
                            setLinkDraft((prev) => ({ ...prev, link_type: folder }));
                            setIsAddLinkOpen(true);
                          }}
                          data-testid={`project-detail-link-folder-add-${folder.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
                        >
                          +
                        </button>
                      </div>
                    ) : null}
                  </div>
                  <div
                    className="grid grid-cols-1 gap-2 p-3 md:grid-cols-3"
                    style={{ display: expandedFolders[folder] ? undefined : "none" }}
                    data-testid={`project-detail-link-folder-grid-${folder.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
                  >
                    {rows.map((link) => {
                      const visual = detectLinkVisual(link);
                      return (
                        <div key={link.id} className="rounded-xl border border-slate-200 p-3" data-testid={`project-detail-link-card-${link.id}`}>
                          <div
                            style={{
                              height: 48,
                              borderRadius: 10,
                              background: visual.bg,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "0 12px",
                              marginBottom: 10,
                            }}
                            data-testid={`project-detail-link-thumbnail-${link.id}`}
                          >
                            <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={visual.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-testid={`project-detail-link-icon-${link.id}`}>
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <path d="M9 13h6" />
                                <path d="M9 17h6" />
                              </svg>
                              <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: ".04em", color: visual.color }} data-testid={`project-detail-link-visual-type-${link.id}`}>
                                {visual.label}
                              </span>
                            </div>
                            {!isViewerOnly ? (
                              <div className="flex items-center gap-1" data-testid={`project-detail-link-actions-${link.id}`}>
                                <button
                                  type="button"
                                  onClick={() => handleRenameLink(link)}
                                  style={{ border: "none", background: "transparent", color: "#94A3B8", cursor: "pointer", padding: 2 }}
                                  data-testid={`project-detail-rename-link-${link.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteLink(link.id)}
                                  style={{ border: "none", background: "transparent", color: "#94A3B8", cursor: "pointer", padding: 2 }}
                                  data-testid={`project-detail-delete-link-${link.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            ) : null}
                          </div>
                          <p className="truncate text-sm font-semibold text-slate-900" data-testid={`project-detail-link-title-${link.id}`}>{link.title}</p>
                          <span
                            className="chip"
                            style={{ marginTop: 6, background: visual.bg, color: visual.color, borderColor: visual.color }}
                            data-testid={`project-detail-link-type-${link.id}`}
                          >
                            {link.link_type}
                          </span>
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noreferrer"
                            className="doc-pill"
                            style={{ marginTop: 10, display: "inline-flex" }}
                            data-testid={`project-detail-open-link-${link.id}`}
                          >
                            Open link ↗
                          </a>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </CardContent>
          ) : null}
        </Card>

        <Card className="jump-card border-0" data-testid="project-detail-cvt-card">
          <CardHeader className="tracker-header !flex !flex-row !items-center !justify-between" style={{ background: "#7C3AED", borderBottom: 0 }}>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => toggleSection("cvt")}
                style={{ border: "none", background: "transparent", color: "#fff", padding: 0, display: "inline-flex", alignItems: "center", cursor: "pointer" }}
                data-testid="project-detail-cvt-accordion-toggle"
              >
                {expandedSections.cvt ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
              <span className="tracker-title" style={{ color: "#fff" }}>Client Value Tracker</span>
              <span className="chip" style={{ background: "rgba(255,255,255,.22)", color: "#fff" }} data-testid="project-detail-cvt-total-completion-badge">{completionPct}%</span>
              <span className="chip" style={{ background: "rgba(255,255,255,.18)", color: "#fff" }} data-testid="project-detail-cvt-template-chip">Spec: {commandCenter.service_label}</span>
            </div>
            <span className="tracker-pct" data-testid="project-detail-cvt-completion-badge">{cvtCompletionPct}% complete</span>
          </CardHeader>
          {expandedSections.cvt ? (
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="data-table" data-testid="project-detail-cvt-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Particular</th>
                    <th style={{ minWidth: 150 }}>Deadline</th>
                    <th style={{ minWidth: 130 }}>Done</th>
                    <th style={{ minWidth: 150 }}>Work Link</th>
                    <th style={{ minWidth: 150 }}>Date Completed</th>
                    <th style={{ minWidth: 120 }}>Status</th>
                    <th style={{ minWidth: 180 }}>Assigned To</th>
                  </tr>
                </thead>
                <tbody>
                  {cvtRows.map((row) => (
                    <tr key={row.key} data-testid={`project-detail-cvt-row-${row.key}`}>
                      <td>{row.sectionIndex}</td>
                      <td>{row.label}</td>
                      <td>
                        <Input
                          type="date"
                          value={deadlineDraftMap[row.key] || ""}
                          onChange={(event) => setDeadlineDraftMap((prev) => ({ ...prev, [row.key]: event.target.value }))}
                          onBlur={() => saveDeadline(row)}
                          disabled={isViewerOnly}
                          className="h-8 w-[140px]"
                          data-testid={`project-detail-cvt-deadline-input-${row.key}`}
                        />
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <div className="flex items-center gap-2" data-testid={`project-detail-cvt-done-toggle-wrap-${row.key}`}>
                          <Switch
                            checked={Boolean(row.activity.completed)}
                            onCheckedChange={(checked) => handleDoneToggle(row, checked)}
                            disabled={isViewerOnly}
                            data-testid={`project-detail-cvt-done-toggle-${row.key}`}
                          />
                          <span className={`text-xs font-semibold ${row.activity.completed ? "text-emerald-700" : "text-slate-500"}`} data-testid={`project-detail-cvt-done-label-${row.key}`}>
                            {row.activity.completed ? "Done" : "Mark Done"}
                          </span>
                        </div>
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {row.activity.work_link ? (
                          <div className="flex items-center gap-2">
                            <a href={row.activity.work_link} target="_blank" rel="noreferrer" className="doc-pill" style={{ whiteSpace: "nowrap" }} data-testid={`project-detail-cvt-work-link-${row.key}`}>
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
                              View proof
                            </a>
                            {!isViewerOnly ? (
                              <button
                                type="button"
                                className="doc-add"
                                style={{ whiteSpace: "nowrap" }}
                                onClick={() => openRowLinkModal(row)}
                                data-testid={`project-detail-cvt-work-link-edit-${row.key}`}
                              >
                                Edit
                              </button>
                            ) : null}
                          </div>
                        ) : (
                          isViewerOnly ? (
                            <span className="text-xs text-slate-400" data-testid={`project-detail-cvt-work-link-readonly-${row.key}`}>—</span>
                          ) : (
                            <button
                              type="button"
                              className="doc-add"
                              style={{ whiteSpace: "nowrap" }}
                              onClick={() => openRowLinkModal(row)}
                              data-testid={`project-detail-cvt-work-link-empty-${row.key}`}
                            >
                              + Add link
                            </button>
                          )
                        )}
                      </td>
                      <td style={{ whiteSpace: "nowrap" }} data-testid={`project-detail-cvt-date-${row.key}`}>{row.activity.date_completed || "—"}</td>
                      <td data-testid={`project-detail-cvt-status-${row.key}`}>{statusChip(row.activity, deadlineDraftMap[row.key] || row.activity.deadline_date)}</td>
                      <td>
                        <Input
                          value={assigneeDraftMap[row.key] || ""}
                          onChange={(event) => setAssigneeDraftMap((prev) => ({ ...prev, [row.key]: event.target.value }))}
                          onBlur={() => saveAssignee(row)}
                          disabled={isViewerOnly}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              saveAssignee(row);
                            }
                          }}
                          list="project-detail-assignee-suggestions"
                          placeholder=""
                          className="h-8 w-[160px]"
                          data-testid={`project-detail-cvt-assignee-input-${row.key}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2 text-xs text-rose-600" data-testid="project-detail-cvt-validation-note">
              {isViewerOnly ? (
                <>ⓘ You have read-only access to this tracker.</>
              ) : (
                <>ⓘ Marking done requires <strong>deadline</strong>, <strong>assigned to</strong>, completion date, and proof link.</>
              )}
            </div>
          </CardContent>
          ) : null}
        </Card>

        <Card className="jump-card border-0" data-testid="project-detail-activity-card">
          <CardHeader className="tracker-header !flex !flex-row !items-center !justify-between" style={{ background: "#4338CA", borderBottom: 0 }}>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => toggleSection("activity")}
                style={{ border: "none", background: "transparent", color: "#fff", padding: 0, display: "inline-flex", alignItems: "center", cursor: "pointer" }}
                data-testid="project-detail-activity-accordion-toggle"
              >
                {expandedSections.activity ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
              <span className="tracker-title" style={{ color: "#fff" }}>Activity Tracker</span>
              <span className="chip" style={{ background: "rgba(255,255,255,.22)", color: "#fff" }}>{completionPct}%</span>
              <span className="chip" style={{ background: "rgba(255,255,255,.18)", color: "#fff" }} data-testid="project-detail-activity-template-chip">Spec: {commandCenter.service_label}</span>
            </div>
            <div className="flex items-center gap-2" data-testid="project-detail-activity-header-actions">
              {isFixedProject && canManageLifecycle ? (
                <div className="flex items-center gap-2" data-testid="project-detail-activity-add-cta-wrap">
                  <Input
                    value={newActivityCtaLabel}
                    onChange={(event) => setNewActivityCtaLabel(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        handleAddActivityCta();
                      }
                    }}
                    placeholder="Add Task"
                    className="h-8 w-[230px] border-white/20 bg-white text-slate-900"
                    data-testid="project-detail-activity-add-cta-input"
                  />
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={handleAddActivityCta}
                    disabled={isAddingActivityCta}
                    data-testid="project-detail-activity-add-cta-button"
                  >
                    {isAddingActivityCta ? "Adding..." : "Add"}
                  </button>
                </div>
              ) : null}
              <span className="tracker-pct" data-testid="project-detail-activity-completion-badge">{activityCompletionPct}% complete</span>
            </div>
          </CardHeader>
          {expandedSections.activity ? (
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="data-table" data-testid="project-detail-activity-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Particular</th>
                    <th style={{ minWidth: 150 }}>Deadline</th>
                    <th style={{ minWidth: 130 }}>Done</th>
                    <th style={{ minWidth: 150 }}>Work Link</th>
                    <th style={{ minWidth: 150 }}>Date Completed</th>
                    <th style={{ minWidth: 120 }}>Status</th>
                    <th style={{ minWidth: 180 }}>Assigned To</th>
                  </tr>
                </thead>
                <tbody>
                  {actRows.map((row) => (
                    <tr key={row.key} data-testid={`project-detail-activity-row-${row.key}`}>
                      <td>{row.sectionIndex}</td>
                      <td>{row.label}</td>
                      <td>
                        <Input
                          type="date"
                          value={deadlineDraftMap[row.key] || ""}
                          onChange={(event) => setDeadlineDraftMap((prev) => ({ ...prev, [row.key]: event.target.value }))}
                          onBlur={() => saveDeadline(row)}
                          disabled={isViewerOnly}
                          className="h-8 w-[140px]"
                          data-testid={`project-detail-activity-deadline-input-${row.key}`}
                        />
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <div className="flex items-center gap-2" data-testid={`project-detail-activity-done-toggle-wrap-${row.key}`}>
                          <Switch
                            checked={Boolean(row.activity.completed)}
                            onCheckedChange={(checked) => handleDoneToggle(row, checked)}
                            disabled={isViewerOnly}
                            data-testid={`project-detail-activity-done-toggle-${row.key}`}
                          />
                          <span className={`text-xs font-semibold ${row.activity.completed ? "text-emerald-700" : "text-slate-500"}`} data-testid={`project-detail-activity-done-label-${row.key}`}>
                            {row.activity.completed ? "Done" : "Mark Done"}
                          </span>
                        </div>
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {row.activity.work_link ? (
                          <div className="flex items-center gap-2">
                            <a href={row.activity.work_link} target="_blank" rel="noreferrer" className="doc-pill" style={{ whiteSpace: "nowrap" }} data-testid={`project-detail-activity-work-link-${row.key}`}>
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
                              View proof
                            </a>
                            {!isViewerOnly ? (
                              <button
                                type="button"
                                className="doc-add"
                                style={{ whiteSpace: "nowrap" }}
                                onClick={() => openRowLinkModal(row)}
                                data-testid={`project-detail-activity-work-link-edit-${row.key}`}
                              >
                                Edit
                              </button>
                            ) : null}
                          </div>
                        ) : (
                          isViewerOnly ? (
                            <span className="text-xs text-slate-400" data-testid={`project-detail-activity-work-link-readonly-${row.key}`}>—</span>
                          ) : (
                            <button
                              type="button"
                              className="doc-add"
                              style={{ whiteSpace: "nowrap" }}
                              onClick={() => openRowLinkModal(row)}
                              data-testid={`project-detail-activity-work-link-empty-${row.key}`}
                            >
                              + Add link
                            </button>
                          )
                        )}
                      </td>
                      <td style={{ whiteSpace: "nowrap" }} data-testid={`project-detail-activity-date-${row.key}`}>{row.activity.date_completed || "—"}</td>
                      <td data-testid={`project-detail-activity-status-${row.key}`}>{statusChip(row.activity, deadlineDraftMap[row.key] || row.activity.deadline_date)}</td>
                      <td>
                        <Input
                          value={assigneeDraftMap[row.key] || ""}
                          onChange={(event) => setAssigneeDraftMap((prev) => ({ ...prev, [row.key]: event.target.value }))}
                          onBlur={() => saveAssignee(row)}
                          disabled={isViewerOnly}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              saveAssignee(row);
                            }
                          }}
                          list="project-detail-assignee-suggestions"
                          placeholder=""
                          className="h-8 w-[160px]"
                          data-testid={`project-detail-activity-assignee-input-${row.key}`}
                        />
                      </td>
                    </tr>
                  ))}
                  {!actRows.length ? (
                    <tr>
                      <td colSpan={8} style={{ color: "var(--mute-500)", textAlign: "center", height: 64 }} data-testid="project-detail-activity-empty-row">
                        No activity rows yet.{isFixedProject && canManageLifecycle ? " Use ‘Add Task’ above to create one." : ""}
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2 text-xs text-rose-600" data-testid="project-detail-activity-validation-note">
              {isViewerOnly ? (
                <>ⓘ You have read-only access to this tracker.</>
              ) : (
                <>ⓘ Marking done requires <strong>deadline</strong>, <strong>assigned to</strong>, completion date, and proof link.</>
              )}
            </div>
          </CardContent>
          ) : null}
        </Card>

        {isFixedProject ? (
          <Card className="jump-card border border-teal-200 bg-white" data-testid="project-detail-fixed-milestones-card">
            <CardHeader style={{ paddingBottom: 10 }}>
              <button
                type="button"
                onClick={() => toggleSection("fixedMilestones")}
                className="flex w-full items-center justify-between gap-2 text-left"
                style={{ border: "none", background: "transparent", padding: 0 }}
                data-testid="project-detail-fixed-milestones-toggle"
              >
                <div>
                  <CardTitle className="text-base" data-testid="project-detail-fixed-milestones-title">Fixed Milestones (POD Delivery)</CardTitle>
                  <p className="text-xs text-slate-500" data-testid="project-detail-fixed-milestones-subtitle">Mark delivery here so Finance can see “Ready to invoice”.</p>
                </div>
                {expandedSections.fixedMilestones ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
              </button>
            </CardHeader>

            {expandedSections.fixedMilestones ? (
              <CardContent>
                {fixedMilestones.length ? (
                  <div className="overflow-x-auto">
                    <table className="data-table" data-testid="project-detail-fixed-milestones-table">
                      <thead>
                        <tr>
                          <th>M#</th>
                          <th>Milestone</th>
                          <th>Trigger</th>
                          <th>%</th>
                          <th>Value</th>
                          <th>Expected</th>
                          <th>Delivered</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fixedMilestones.map((milestone) => (
                          <tr key={milestone.id} data-testid={`project-detail-fixed-milestone-row-${milestone.id}`}>
                            <td>M{milestone.milestone_no}</td>
                            <td>{milestone.name}</td>
                            <td>{milestone.trigger || "—"}</td>
                            <td>{Number(milestone.percentage || 0).toFixed(1)}%</td>
                            <td>{Number(milestone.value || 0).toLocaleString("en-IN", { style: "currency", currency: projectCurrency, maximumFractionDigits: 0 })}</td>
                            <td>{milestone.expected_delivery_date || "—"}</td>
                            <td>{milestone.delivered_at || "—"}</td>
                            <td>
                              <span className={`chip ${milestone.status?.status === "ready_to_invoice" ? "chip-warn" : milestone.status?.status === "paid" ? "chip-ok" : milestone.status?.status === "overdue" ? "chip-crit" : "chip-mute"}`}>
                                {milestone.status?.label || "Pending"}
                              </span>
                            </td>
                            <td>
                              {!milestone.delivered_at && canManageLifecycle ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => markMilestoneDelivered(milestone.id)}
                                  data-testid={`project-detail-fixed-milestone-mark-delivered-${milestone.id}`}
                                >
                                  Mark delivered
                                </Button>
                              ) : (
                                <span className="text-xs text-slate-500">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500" data-testid="project-detail-fixed-milestones-empty">No fixed milestones yet.</p>
                )}

                {canManageLifecycle ? (
                  <div className="mt-4 rounded-lg border border-dashed border-teal-300 bg-teal-50 p-3" data-testid="project-detail-fixed-add-milestone-wrap">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-xs text-teal-700" data-testid="project-detail-fixed-allocation-summary">
                        Allocated: {fixedAllocatedPercent}% of contract ({Number(fixedContractValue || 0).toLocaleString("en-IN", { style: "currency", currency: projectCurrency, maximumFractionDigits: 0 })})
                      </p>
                      <Button type="button" size="sm" onClick={openAddFixedMilestoneModal} data-testid="project-detail-fixed-add-milestone-button">+ Add milestone</Button>
                    </div>
                  </div>
                ) : null}
              </CardContent>
            ) : null}
          </Card>
        ) : null}

        {user.role === "super_admin" ? (
          <Card className="jump-card border border-slate-200 bg-white" data-testid="project-detail-recent-activity-card">
            <CardHeader style={{ paddingBottom: 10 }}>
              <button
                type="button"
                onClick={() => toggleSection("recentActivity")}
                className="flex w-full items-center justify-between gap-2 text-left"
                style={{ border: "none", background: "transparent", padding: 0 }}
                data-testid="project-detail-recent-activity-toggle"
              >
                <div>
                  <CardTitle className="text-base" data-testid="project-detail-recent-activity-title">Recent Activity</CardTitle>
                  <p className="text-xs text-slate-500" data-testid="project-detail-recent-activity-subtitle">Last 10 audit events</p>
                </div>
                {expandedSections.recentActivity ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
              </button>
            </CardHeader>
            {expandedSections.recentActivity ? (
              <CardContent>
                <div className="space-y-2" data-testid="project-detail-recent-activity-list">
                  {recentAuditRows.length ? recentAuditRows.map((row) => (
                    <div key={row.id} className="rounded-lg border border-slate-200 px-3 py-2" data-testid={`project-detail-recent-activity-row-${row.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-slate-900" data-testid={`project-detail-recent-activity-action-${row.id}`}>{row.action}</p>
                        <span className={`chip ${row.status === "success" ? "chip-ok" : "chip-crit"}`} data-testid={`project-detail-recent-activity-status-${row.id}`}>{row.status}</span>
                      </div>
                      <p className="mt-1 text-xs text-slate-500" data-testid={`project-detail-recent-activity-meta-${row.id}`}>
                        {(row.actor?.email || row.actor?.name || "system")} · {new Date(row.created_at).toLocaleString("en-IN")}
                      </p>
                      {row.reason ? <p className="mt-1 text-xs text-slate-600" data-testid={`project-detail-recent-activity-reason-${row.id}`}>{row.reason}</p> : null}
                    </div>
                  )) : <p className="text-sm text-slate-500" data-testid="project-detail-recent-activity-empty">No recent events.</p>}
                </div>
              </CardContent>
            ) : null}
          </Card>
        ) : null}
      </section>

      {canManageTeam ? (
        <>
          <button
            type="button"
            aria-label="Close Manage Team drawer"
            className={`modal-backdrop ${isManageTeamOpen ? "open" : ""}`}
            style={{ zIndex: 65, border: "none", cursor: "default" }}
            onClick={closeManageTeamDrawer}
            data-testid="project-detail-manage-team-backdrop"
          />

          <aside
            className={`task-drawer ${isManageTeamOpen ? "open" : ""}`}
            style={{ width: 460, zIndex: 70 }}
            data-testid="project-detail-manage-team-drawer"
          >
            <div className="drawer-header" data-testid="project-detail-manage-team-drawer-header">
              <div className="inline-flex items-center gap-2">
                <Users className="h-4 w-4 text-slate-700" />
                <div>
                  <p className="text-sm font-semibold text-slate-900" data-testid="project-detail-manage-team-title">Manage Team</p>
                  <p className="text-xs text-slate-500" data-testid="project-detail-manage-team-subtitle">{members.length} active member(s)</p>
                </div>
              </div>
              <button
                type="button"
                className="btn btn-sm"
                onClick={closeManageTeamDrawer}
                data-testid="project-detail-manage-team-close-button"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            <div className="drawer-body" data-testid="project-detail-manage-team-drawer-body">
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="project-detail-manage-team-warning-banner">
                <p className="text-[12px] font-semibold text-amber-800">Access rule</p>
                <p className="text-xs text-amber-700">
                  Only <strong>super_admin</strong> and <strong>project_admin</strong> can add, remove, or change member roles.
                </p>
              </div>

              <div className="mt-3 rounded-lg border border-slate-200 p-3" data-testid="project-detail-manage-team-add-section">
                <div className="mb-2 inline-flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <UserPlus className="h-4 w-4" />
                  Add member via search
                </div>

                <div className="relative" data-testid="project-detail-manage-team-search-wrap">
                  <Input
                    value={memberSearchQuery}
                    onChange={(event) => handleMemberSearchChange(event.target.value)}
                    placeholder="Search name/email or type email"
                    data-testid="project-detail-manage-team-search-input"
                  />

                  {memberSearchQuery.trim().length >= 2 ? (
                    <div
                      className="absolute left-0 right-0 top-[calc(100%+6px)] rounded-xl border border-slate-200 bg-white shadow-lg"
                      style={{ zIndex: 90, maxHeight: 220, overflowY: "auto" }}
                      data-testid="project-detail-manage-team-suggestions-dropdown"
                    >
                      {isLoadingMemberSuggestions ? (
                        <p className="px-3 py-2 text-xs text-slate-500" data-testid="project-detail-manage-team-suggestions-loading">Searching…</p>
                      ) : null}

                      {!isLoadingMemberSuggestions && memberSuggestions.map((suggestion) => (
                        <button
                          key={suggestion.id}
                          type="button"
                          className="flex w-full items-center justify-between border-b border-slate-100 px-3 py-2 text-left text-xs hover:bg-slate-50"
                          onClick={() => selectMemberSuggestion(suggestion)}
                          data-testid={`project-detail-manage-team-suggestion-${suggestion.id}`}
                        >
                          <span className="font-medium text-slate-800">{suggestion.name || formatMemberName(suggestion.email)}</span>
                          <span className="text-slate-500">{suggestion.email}</span>
                        </button>
                      ))}

                      {!isLoadingMemberSuggestions && memberSuggestions.length === 0 ? (
                        manualEmailCandidate ? (
                          <button
                            type="button"
                            className="flex w-full items-center justify-between px-3 py-2 text-left text-xs hover:bg-slate-50"
                            onClick={() => {
                              setSelectedMemberSuggestion(null);
                              setMemberSearchQuery(manualEmailCandidate);
                            }}
                            data-testid="project-detail-manage-team-manual-email-option"
                          >
                            <span className="font-medium text-slate-800">Add by email</span>
                            <span className="text-slate-500">{manualEmailCandidate}</span>
                          </button>
                        ) : (
                          <p className="px-3 py-2 text-xs text-slate-500" data-testid="project-detail-manage-team-suggestions-empty">No matching users found</p>
                        )
                      ) : null}
                    </div>
                  ) : null}
                </div>

                <div className="mt-2 flex items-center justify-between" data-testid="project-detail-manage-team-selected-user-row">
                  <p className="text-xs text-slate-500" data-testid="project-detail-manage-team-selected-user-text">
                    {selectedMemberSuggestion
                      ? `Selected: ${selectedMemberSuggestion.name || formatMemberName(selectedMemberSuggestion.email)} (${selectedMemberSuggestion.email})`
                      : manualEmailCandidate
                        ? `Manual email: ${manualEmailCandidate}`
                        : "Select a user suggestion or enter a valid email."}
                  </p>
                </div>

                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto]" data-testid="project-detail-manage-team-controls-row">
                  <label className="text-xs text-slate-600" htmlFor="manage-team-role-select" data-testid="project-detail-manage-team-role-label">
                    Role
                    <select
                      id="manage-team-role-select"
                      className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
                      value={selectedMemberRole}
                      onChange={(event) => setSelectedMemberRole(event.target.value)}
                      data-testid="project-detail-manage-team-role-select"
                    >
                      {roleOptionsForCurrentUser.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>

                  <Button
                    type="button"
                    className="self-end"
                    onClick={handleAddMemberToProject}
                    disabled={!canSubmitMemberInvite || isAddingMember}
                    data-testid="project-detail-manage-team-add-button"
                  >
                    {isAddingMember ? "Adding..." : "Add member"}
                  </Button>
                </div>
              </div>

              <div className="mt-4" data-testid="project-detail-manage-team-members-section">
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500" data-testid="project-detail-manage-team-members-title">
                  Current members
                </p>

                <div className="space-y-2" data-testid="project-detail-manage-team-members-list">
                  {members.map((member) => {
                    const isProjectAdminMember = member.role === "project_admin";
                    const isRoleLocked = isProjectAdminMember && user.role !== "super_admin";
                    const roleDraft = memberRoleDraftMap[member.id] || member.role;
                    const roleChanged = roleDraft !== member.role;
                    const isRoleBusy = busyMemberActionId === `role-${member.id}`;
                    const isRemoveBusy = busyMemberActionId === `remove-${member.id}`;
                    const removeDisabled = member.user_email === user.email || (isProjectAdminMember && user.role !== "super_admin");

                    return (
                      <div key={member.id} className="rounded-lg border border-slate-200 p-3" data-testid={`project-detail-manage-team-member-row-${member.id}`}>
                        <div className="mb-2 flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <div className="avatar avatar-sm avatar-a" data-testid={`project-detail-manage-team-member-avatar-${member.id}`}>
                              {memberInitials(member.user_email)}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-slate-900" data-testid={`project-detail-manage-team-member-name-${member.id}`}>
                                {formatMemberName(member.user_email)}
                              </p>
                              <p className="text-xs text-slate-500" data-testid={`project-detail-manage-team-member-email-${member.id}`}>{member.user_email}</p>
                            </div>
                          </div>
                          <span className="chip chip-mute" data-testid={`project-detail-manage-team-member-role-chip-${member.id}`}>
                            {TEAM_ROLE_LABEL[member.role] || member.role}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto_auto]" data-testid={`project-detail-manage-team-member-actions-${member.id}`}>
                          <select
                            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
                            value={roleDraft}
                            onChange={(event) => setMemberRoleDraftMap((prev) => ({ ...prev, [member.id]: event.target.value }))}
                            disabled={isRoleLocked || isRoleBusy || isRemoveBusy}
                            data-testid={`project-detail-manage-team-member-role-select-${member.id}`}
                          >
                            {roleOptionsForCurrentUser.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>

                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => saveMemberRole(member)}
                            disabled={!roleChanged || isRoleLocked || isRoleBusy || isRemoveBusy}
                            data-testid={`project-detail-manage-team-member-save-role-${member.id}`}
                          >
                            {isRoleBusy ? "Saving..." : "Save role"}
                          </Button>

                          <Button
                            type="button"
                            variant="outline"
                            style={{ color: "var(--crit-700)", borderColor: "#fecaca" }}
                            onClick={() => removeMemberFromProject(member)}
                            disabled={removeDisabled || isRoleBusy || isRemoveBusy}
                            data-testid={`project-detail-manage-team-member-remove-${member.id}`}
                          >
                            {isRemoveBusy ? "Removing..." : "Remove"}
                          </Button>
                        </div>

                        {removeDisabled ? (
                          <p className="mt-2 text-xs text-slate-500" data-testid={`project-detail-manage-team-member-hint-${member.id}`}>
                            {member.user_email === user.email
                              ? "You cannot remove yourself from this project."
                              : "Project admin memberships can only be modified by super admins."}
                          </p>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </aside>
        </>
      ) : null}

      {!canManageTeam ? (
        <>
          <button
            type="button"
            aria-label="Close Team viewer drawer"
            className={`modal-backdrop ${isViewerTeamOpen ? "open" : ""}`}
            style={{ zIndex: 65, border: "none", cursor: "default" }}
            onClick={closeViewerTeamDrawer}
            data-testid="project-detail-viewer-team-backdrop"
          />

          <aside
            className={`task-drawer ${isViewerTeamOpen ? "open" : ""}`}
            style={{ width: 420, zIndex: 70 }}
            data-testid="project-detail-viewer-team-drawer"
          >
            <div className="drawer-header" data-testid="project-detail-viewer-team-drawer-header">
              <div className="inline-flex items-center gap-2">
                <Users className="h-4 w-4 text-slate-700" />
                <div>
                  <p className="text-sm font-semibold text-slate-900" data-testid="project-detail-viewer-team-title">Project team</p>
                  <p className="text-xs text-slate-500" data-testid="project-detail-viewer-team-subtitle">{members.length} active member(s)</p>
                </div>
              </div>
              <button type="button" className="btn btn-sm" onClick={closeViewerTeamDrawer} data-testid="project-detail-viewer-team-close-button">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            <div className="drawer-body" data-testid="project-detail-viewer-team-drawer-body">
              <div className="space-y-2" data-testid="project-detail-viewer-team-members-list">
                {members.map((member) => (
                  <div key={member.id} className="rounded-lg border border-slate-200 p-3" data-testid={`project-detail-viewer-team-member-row-${member.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className="avatar avatar-sm avatar-a" data-testid={`project-detail-viewer-team-member-avatar-${member.id}`}>
                          {memberInitials(member.user_email)}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-900" data-testid={`project-detail-viewer-team-member-name-${member.id}`}>{formatMemberName(member.user_email)}</p>
                          <p className="text-xs text-slate-500" data-testid={`project-detail-viewer-team-member-email-${member.id}`}>{member.user_email}</p>
                        </div>
                      </div>
                      <span className="chip chip-mute" data-testid={`project-detail-viewer-team-member-role-${member.id}`}>{TEAM_ROLE_LABEL[member.role] || member.role}</span>
                    </div>
                  </div>
                ))}
              </div>

              {isViewerOnly ? (
                <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="project-detail-viewer-team-access-request-card">
                  <p className="text-[12px] font-semibold text-amber-800" data-testid="project-detail-viewer-team-access-request-title">
                    To contribute to this project, ask {projectAdminName || "the admin"} to add you as a member.
                  </p>
                  <p className="mt-1 text-xs text-amber-700" data-testid="project-detail-viewer-team-access-request-copy">
                    You can view everything on this project in read-only mode.
                  </p>
                  {requestAccessHref ? (
                    <a href={requestAccessHref} className="btn btn-sm mt-3 inline-flex" data-testid="project-detail-viewer-team-request-access-button">
                      Request access
                    </a>
                  ) : null}
                </div>
              ) : (
                <p className="mt-4 text-xs text-slate-500" data-testid="project-detail-viewer-team-read-note">
                  Team list is visible to all project members.
                </p>
              )}
            </div>
          </aside>
        </>
      ) : null}

      <datalist id="project-detail-assignee-suggestions" data-testid="project-detail-assignee-suggestions">
        {assigneeSuggestions.map((option) => (
          <option key={option} value={option} />
        ))}
      </datalist>

      <datalist id="project-detail-folder-options" data-testid="project-detail-folder-options">
        {folderOptions.map((option) => (
          <option key={option} value={option} />
        ))}
      </datalist>

      <Dialog open={completionModal.open} onOpenChange={(open) => setCompletionModal((prev) => ({ ...prev, open }))}>
        <DialogContent className="sm:max-w-[480px]" style={{ padding: 0 }} data-testid="project-detail-completion-modal">
          <DialogHeader style={{ padding: "18px 22px", borderBottom: "1px solid var(--paper-200)" }}>
            <DialogTitle data-testid="project-detail-completion-modal-title">Mark as Done</DialogTitle>
            <DialogDescription data-testid="project-detail-completion-modal-description">{completionModal.label}</DialogDescription>
          </DialogHeader>

          <div className="space-y-3" style={{ padding: "16px 22px" }}>
            <div>
              <div style={{ fontSize: 12.5, background: "var(--paper-50)", border: "1px solid var(--paper-200)", borderRadius: 8, padding: "10px 14px", color: "var(--ink-800)" }} data-testid="project-detail-completion-item-box">
                {completionModal.label}
              </div>
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500" data-testid="project-detail-completion-date-label">Date of completion *</p>
              <Input
                type="date"
                value={completionModal.date_completed}
                onChange={(event) => setCompletionModal((prev) => ({ ...prev, date_completed: event.target.value }))}
                data-testid="project-detail-completion-date-input"
              />
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500" data-testid="project-detail-completion-work-link-label">Proof of work link *</p>
              <Input
                value={completionModal.work_link}
                onChange={(event) => setCompletionModal((prev) => ({ ...prev, work_link: event.target.value }))}
                placeholder="https://drive.google.com/..."
                style={{ fontFamily: "var(--font-mono)" }}
                data-testid="project-detail-completion-work-link-input"
              />
            </div>
          </div>

          <DialogFooter style={{ borderTop: "1px solid var(--paper-200)", padding: "12px 22px" }}>
            <Button variant="outline" onClick={() => setCompletionModal((prev) => ({ ...prev, open: false }))} data-testid="project-detail-completion-cancel-button">
              Cancel
            </Button>
            <Button onClick={submitCompletion} style={{ background: "var(--ok-500)", borderColor: "var(--ok-500)", color: "#fff" }} data-testid="project-detail-completion-confirm-button">
              Confirm Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rowLinkModal.open} onOpenChange={(open) => setRowLinkModal((prev) => ({ ...prev, open }))}>
        <DialogContent className="sm:max-w-[560px]" style={{ padding: 0 }} data-testid="project-detail-row-add-link-modal">
          <DialogHeader style={{ padding: "18px 22px", borderBottom: "1px solid var(--paper-200)" }}>
            <DialogTitle data-testid="project-detail-row-add-link-modal-title">{rowLinkModal.work_link ? "Update Work Link" : "Add Work Link"}</DialogTitle>
            <DialogDescription data-testid="project-detail-row-add-link-modal-description">{rowLinkModal.label}</DialogDescription>
          </DialogHeader>

          <div className="space-y-3" style={{ padding: "16px 22px" }}>
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">URL *</p>
              <Input
                value={rowLinkModal.work_link}
                onChange={(event) => setRowLinkModal((prev) => ({ ...prev, work_link: event.target.value }))}
                placeholder="https://drive.google.com/..."
                data-testid="project-detail-row-add-link-url-input"
              />
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500" data-testid="project-detail-row-add-link-date-label">Date completed *</p>
              <Input
                type="date"
                value={rowLinkModal.date_completed}
                onChange={(event) => setRowLinkModal((prev) => ({ ...prev, date_completed: event.target.value }))}
                data-testid="project-detail-row-add-link-date-input"
              />
            </div>
          </div>

          <DialogFooter style={{ borderTop: "1px solid var(--paper-200)", padding: "12px 22px" }}>
            <Button
              variant="outline"
              type="button"
              onClick={() => setRowLinkModal((prev) => ({ ...prev, open: false }))}
              data-testid="project-detail-row-add-link-cancel-button"
            >
              Cancel
            </Button>
            <Button type="button" onClick={submitRowLink} data-testid="project-detail-row-add-link-submit-button">
              {rowLinkModal.work_link ? "Update Link" : "Add Link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isAddFixedMilestoneModalOpen} onOpenChange={setIsAddFixedMilestoneModalOpen}>
        <DialogContent className="max-w-xl" data-testid="project-detail-add-fixed-milestone-modal">
          <DialogHeader>
            <DialogTitle data-testid="project-detail-add-fixed-milestone-title">Add milestone</DialogTitle>
            <DialogDescription data-testid="project-detail-add-fixed-milestone-subtitle">
              {project?.name} · {project?.client_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3" data-testid="project-detail-add-fixed-milestone-fields">
            <div className="grid grid-cols-[120px_1fr] gap-3">
              <label className="space-y-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Milestone #
                <Input
                  type="number"
                  min={1}
                  value={newFixedMilestoneDraft.milestone_no}
                  onChange={(event) => setNewFixedMilestoneDraft((prev) => ({ ...prev, milestone_no: Number(event.target.value || 1) }))}
                  data-testid="project-detail-add-fixed-milestone-no-input"
                />
              </label>
              <label className="space-y-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Milestone name
                <Input value={newFixedMilestoneDraft.name} onChange={(event) => setNewFixedMilestoneDraft((prev) => ({ ...prev, name: event.target.value }))} data-testid="project-detail-add-fixed-milestone-name-input" />
              </label>
            </div>

            <label className="space-y-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Trigger / delivery condition
              <Input value={newFixedMilestoneDraft.trigger} onChange={(event) => setNewFixedMilestoneDraft((prev) => ({ ...prev, trigger: event.target.value }))} data-testid="project-detail-add-fixed-milestone-trigger-input" />
              <p className="text-[11px] font-normal text-slate-500">This is what needs to happen before this milestone can be invoiced.</p>
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="space-y-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Percentage of total
                <Input
                  type="number"
                  min={0}
                  value={newFixedMilestoneDraft.percentage}
                  onChange={(event) => {
                    const nextPercent = Number(event.target.value || 0);
                    const nextValue = fixedContractValue > 0 ? (fixedContractValue * nextPercent) / 100 : 0;
                    setNewFixedMilestoneDraft((prev) => ({ ...prev, percentage: event.target.value, value: nextPercent > 0 ? String(Number(nextValue.toFixed(2))) : "" }));
                  }}
                  data-testid="project-detail-add-fixed-milestone-percent-input"
                />
              </label>

              <label className="space-y-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Milestone value
                <Input
                  type="number"
                  min={0}
                  value={newFixedMilestoneDraft.value}
                  onChange={(event) => {
                    const nextValue = Number(event.target.value || 0);
                    const nextPercent = fixedContractValue > 0 ? (nextValue / fixedContractValue) * 100 : 0;
                    setNewFixedMilestoneDraft((prev) => ({ ...prev, value: event.target.value, percentage: nextValue > 0 ? String(Number(nextPercent.toFixed(2))) : "" }));
                  }}
                  data-testid="project-detail-add-fixed-milestone-value-input"
                />
              </label>
            </div>

            <div className={`rounded-lg border px-3 py-2 text-xs font-semibold ${fixedAllocatedPercent + Number(newFixedMilestoneDraft.percentage || 0) > 100 || fixedAllocatedPercent >= 100 ? "border-amber-200 bg-amber-50 text-amber-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`} data-testid="project-detail-add-fixed-milestone-allocation-banner">
              {fixedAllocatedPercent + Number(newFixedMilestoneDraft.percentage || 0) > 100 || fixedAllocatedPercent >= 100
                ? `${fixedAllocatedPercent}% already allocated. Adding this milestone exceeds contract allocation.`
                : `${(fixedAllocatedPercent + Number(newFixedMilestoneDraft.percentage || 0)).toFixed(2)}% allocated after this milestone.`}
            </div>

            <label className="space-y-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Expected delivery date (optional)
              <Input type="date" value={newFixedMilestoneDraft.expected_delivery_date} onChange={(event) => setNewFixedMilestoneDraft((prev) => ({ ...prev, expected_delivery_date: event.target.value }))} data-testid="project-detail-add-fixed-milestone-expected-date-input" />
              <p className="text-[11px] font-normal text-slate-500">Does not create a due date until invoice is raised.</p>
            </label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsAddFixedMilestoneModalOpen(false)} data-testid="project-detail-add-fixed-milestone-cancel-button">Cancel</Button>
            <Button
              type="button"
              onClick={saveNewFixedMilestone}
              disabled={!newFixedMilestoneDraft.name.trim() || !newFixedMilestoneDraft.trigger.trim() || (Number(newFixedMilestoneDraft.percentage || 0) <= 0 && Number(newFixedMilestoneDraft.value || 0) <= 0)}
              data-testid="project-detail-add-fixed-milestone-save-button"
            >
              Add milestone
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isAddLinkOpen} onOpenChange={setIsAddLinkOpen}>
        <DialogContent className="sm:max-w-[560px] resource-upload-modal" data-testid="project-detail-add-link-modal">
          <DialogHeader className="resource-upload-modal-header">
            <DialogTitle data-testid="project-detail-add-link-modal-title">Add Link</DialogTitle>
            <DialogDescription data-testid="project-detail-add-link-modal-description">
              Add a file/document link or paste a Google Drive folder URL to auto-import files.
            </DialogDescription>
          </DialogHeader>

          <form className="resource-upload-form" onSubmit={handleCreateLink} data-testid="project-detail-add-link-form">
            <div>
              <p className="resource-upload-label">Title</p>
              <Input
                value={linkDraft.title}
                onChange={(event) => setLinkDraft((prev) => ({ ...prev, title: event.target.value }))}
                placeholder="Link title (optional for folder import)"
                data-testid="project-detail-add-link-title-input"
              />
            </div>

            <div>
              <p className="resource-upload-label">URL</p>
              <Input
                value={linkDraft.url}
                onChange={(event) => setLinkDraft((prev) => ({ ...prev, url: event.target.value }))}
                placeholder="https://..."
                data-testid="project-detail-add-link-url-input"
              />
            </div>

            <div>
              <p className="resource-upload-label">Folder</p>
              <Input
                value={linkDraft.link_type}
                onChange={(event) => setLinkDraft((prev) => ({ ...prev, link_type: event.target.value }))}
                placeholder="Folder name"
                list="project-detail-folder-options"
                data-testid="project-detail-add-link-folder-input"
              />
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }} data-testid="project-detail-add-link-folder-options">
                {folderOptions.map((folder) => (
                  <button
                    key={folder}
                    type="button"
                    className={`chip ${linkDraft.link_type === folder ? "chip-brand" : "chip-mute"}`}
                    onClick={() => setLinkDraft((prev) => ({ ...prev, link_type: folder }))}
                    data-testid={`project-detail-add-link-folder-option-${folder.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
                  >
                    {folder}
                  </button>
                ))}
              </div>
            </div>

            <DialogFooter className="resource-upload-footer">
              <Button variant="outline" type="button" onClick={() => setIsAddLinkOpen(false)} data-testid="project-detail-add-link-cancel-button">
                Cancel
              </Button>
              <Button type="submit" data-testid="project-detail-add-link-submit-button">Add Link</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ProjectLifecycleDialogs
        testIdPrefix="project-detail-lifecycle"
        pauseOpen={isProjectPauseModalOpen}
        onPauseOpenChange={setIsProjectPauseModalOpen}
        archiveOpen={isProjectArchiveModalOpen}
        onArchiveOpenChange={setIsProjectArchiveModalOpen}
        projectName={project?.name || ""}
        clientName={project?.client_name || ""}
        isSaving={isLifecycleLoading}
        onConfirmPause={confirmPauseProject}
        onConfirmArchive={confirmArchiveProject}
      />

    </AppShell>
  );
}
